import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  PaperPlaneTiltIcon,
  ArrowSquareOutIcon,
  PackageIcon,
  CheckIcon,
  FileTextIcon,
} from "@phosphor-icons/react";

import Layout from "../../components/Layout";
import EntityHeader from "../../components/crm/EntityHeader";
import EmptyState from "../../components/EmptyState";
import { ErrorBanner } from "../../components/crm/Form";
import AcknowledgmentStateChip from "../../components/diagnostics/AcknowledgmentStateChip";
import FlagToggleButton from "../../components/diagnostics/FlagToggleButton";
import {
  REPORT_STATUS_LABEL,
  REPORT_STATUS_TINT,
} from "../../lib/diagnostics";
import api, { formatApiError } from "../../lib/api";

const STATUS_FILTERS = [
  { value: "", label: "All" },
  { value: "draft", label: "Draft" },
  { value: "submitted", label: "Submitted" },
  { value: "finalized", label: "Finalized" },
  { value: "reopened", label: "Reopened" },
];

const ACK_FILTERS = [
  { value: "any", label: "All" },
  { value: "acknowledged", label: "Acknowledged" },
  { value: "not_yet", label: "Not yet" },
  { value: "stale", label: "Stale" },
];

const REMINDER_FILTERS = [
  { value: "any", label: "All" },
  { value: "sent_7d", label: "Reminder sent (7d)" },
  { value: "none", label: "No reminder" },
];

const RANGE_FILTERS = [
  { value: "any", label: "All time" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
];

const FLAG_FILTERS = [
  { value: "any", label: "All" },
  { value: "flagged", label: "Flagged" },
  { value: "mine", label: "My flags" },
  { value: "not_flagged", label: "Not flagged" },
];

const NOTIF_TINT = {
  sent: "bg-emerald-100 text-emerald-900 border-emerald-300",
  failed: "bg-red-100 text-red-900 border-red-300",
  stubbed_no_provider: "bg-amber-100 text-amber-900 border-amber-300",
  pending: "bg-slate-100 text-slate-700 border-slate-300",
};

const PILL_BASE =
  "inline-flex items-center rounded-md border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide";

export default function DiagnosticReportsList() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  const status = params.get("status") || "";
  const ack = params.get("ack") || "any";
  const reminder = params.get("reminder") || "any";
  const range = params.get("range") || "any";
  const flag = params.get("flag") || "any";
  const q = params.get("q") || "";

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [err, setErr] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [qInput, setQInput] = useState(q);

  const load = useCallback(async () => {
    setErr("");
    const qs = new URLSearchParams();
    if (status) qs.set("status", status);
    if (ack) qs.set("ack", ack);
    if (reminder) qs.set("reminder", reminder);
    if (range) qs.set("range", range);
    if (flag) qs.set("flag", flag);
    if (q) qs.set("q", q);
    qs.set("limit", "100");
    try {
      const r = await api.get(`/diagnostic-reports?${qs.toString()}`);
      setItems(r.data?.items || []);
      setTotal(r.data?.total || 0);
    } catch (e) {
      setErr(formatApiError(e));
    }
  }, [status, ack, reminder, range, flag, q]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setQInput(q);
  }, [q]);

  const setFilter = (k, v, defaultValue = "") => {
    const next = new URLSearchParams(params);
    if (v && v !== defaultValue) next.set(k, v);
    else next.delete(k);
    setParams(next);
  };

  const submitSearch = (e) => {
    e?.preventDefault?.();
    setFilter("q", qInput.trim());
  };

  const sendReminder = async (row) => {
    setBusyId(row.id);
    try {
      await api.post(
        `/diagnostic-reports/${row.id}/request-acknowledgment`,
        {}
      );
      toast.success("Reminder queued");
      load();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 429) {
        toast.error(
          (detail && detail.message) ||
            "A reminder for this report was already sent today."
        );
      } else if (status === 400 && detail?.error === "no_customer_email") {
        toast.error(
          detail.message ||
            "Customer has no email on file. Add one to send a reminder."
        );
      } else if (status === 409) {
        toast.error("Report must be finalized first.");
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <EntityHeader
          title="Diagnostic Reports"
          subtitle="Triage view: every report across the business, filtered for follow-up"
        />
        <ErrorBanner message={err} />

        <div className="bg-white border border-slate-200 rounded-md p-4 space-y-3">
          <form onSubmit={submitSearch} className="flex flex-wrap gap-2">
            <input
              data-testid="dr-search-input"
              type="text"
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              placeholder="Search by customer, address, work order #, or report id…"
              className="flex-1 min-w-[280px] rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-700 focus:border-slate-700"
            />
            <button
              type="submit"
              data-testid="dr-search-submit"
              className="rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
            >
              Search
            </button>
            {q && (
              <button
                type="button"
                data-testid="dr-search-clear"
                onClick={() => {
                  setQInput("");
                  setFilter("q", "");
                }}
                className="text-sm font-semibold text-slate-700 hover:text-slate-900"
              >
                Clear
              </button>
            )}
          </form>

          <FilterChipRow
            label="Status"
            options={STATUS_FILTERS}
            value={status}
            onChange={(v) => setFilter("status", v)}
            testidPrefix="dr-status"
          />
          <FilterChipRow
            label="Ack"
            options={ACK_FILTERS}
            value={ack}
            onChange={(v) => setFilter("ack", v, "any")}
            testidPrefix="dr-ack"
          />
          <FilterChipRow
            label="Reminder"
            options={REMINDER_FILTERS}
            value={reminder}
            onChange={(v) => setFilter("reminder", v, "any")}
            testidPrefix="dr-reminder"
          />
          <FilterChipRow
            label="Range"
            options={RANGE_FILTERS}
            value={range}
            onChange={(v) => setFilter("range", v, "any")}
            testidPrefix="dr-range"
          />
          <FilterChipRow
            label="Flag"
            options={FLAG_FILTERS}
            value={flag}
            onChange={(v) => setFilter("flag", v, "any")}
            testidPrefix="dr-flag"
          />

          <div className="text-[11px] text-slate-500 pt-1">
            Showing {items.length} of {total}
          </div>
        </div>

        <div
          className="bg-white border border-slate-200 rounded-md overflow-hidden"
          data-testid="dr-list"
        >
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-left text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                <th className="px-2 py-2.5 w-8"></th>
                <th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5">Customer</th>
                <th className="px-4 py-2.5">Property</th>
                <th className="px-4 py-2.5">WO #</th>
                <th className="px-4 py-2.5">Finalized</th>
                <th className="px-4 py-2.5">Acknowledgment</th>
                <th className="px-4 py-2.5">Reminder</th>
                <th className="px-4 py-2.5 text-right">Photos</th>
                <th className="px-4 py-2.5 text-center">RMA</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <EmptyState
                  inTable
                  colSpan={11}
                  icon={FileTextIcon}
                  title={
                    status || ack !== "any" || reminder !== "any" || range !== "any" || flag !== "any" || q
                      ? "No matching diagnostic reports"
                      : "No diagnostic reports yet"
                  }
                  description={
                    status || ack !== "any" || reminder !== "any" || range !== "any" || flag !== "any" || q
                      ? "Try clearing the filters above to see every report."
                      : "Diagnostic reports are created when a tech finishes a work order. Open a completed WO and click 'Create Report'."
                  }
                  testId="dr-empty"
                />
              )}
              {items.map((r) => {
                const tint =
                  REPORT_STATUS_TINT[r.status] ||
                  "bg-slate-100 text-slate-700 border-slate-300";
                const label = REPORT_STATUS_LABEL[r.status] || r.status;
                const finalizedRel = _relative(r.finalized_at);
                const canRemind =
                  r.status === "finalized" &&
                  (!r.latest_reminder ||
                    r.latest_reminder.status === "failed");
                return (
                  <tr
                    key={r.id}
                    data-testid={`dr-row-${r.id}`}
                    data-flagged={r.flagged ? "true" : "false"}
                    onClick={() =>
                      navigate(`/work-orders/${r.work_order_id}/report`)
                    }
                    className={`border-t border-slate-200 hover:bg-slate-50/70 cursor-pointer ${
                      r.flagged ? "border-l-4 border-l-amber-400" : ""
                    }`}
                  >
                    <td className="px-2 py-2.5 align-top">
                      <FlagToggleButton
                        reportId={r.id}
                        flagged={r.flagged}
                        flaggedAt={r.flagged_at}
                        flaggedByName={r.flagged_by_name}
                        testid={`dr-flag-${r.id}`}
                        onChange={load}
                      />
                    </td>
                    <td className="px-4 py-2.5 align-top">
                      <span className={`${PILL_BASE} ${tint}`}>{label}</span>
                    </td>
                    <td className="px-4 py-2.5 align-top text-sm text-slate-900">
                      {r.customer_id ? (
                        <Link
                          to={`/customers/${r.customer_id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="hover:underline"
                        >
                          {r.customer_name || "—"}
                        </Link>
                      ) : (
                        r.customer_name || "—"
                      )}
                    </td>
                    <td className="px-4 py-2.5 align-top text-xs text-slate-700">
                      {r.property_address || "—"}
                    </td>
                    <td className="px-4 py-2.5 align-top">
                      <Link
                        to={`/work-orders/${r.work_order_id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="font-data text-xs text-slate-700 hover:text-slate-900 hover:underline"
                      >
                        {r.work_order_short}
                      </Link>
                    </td>
                    <td className="px-4 py-2.5 align-top text-xs text-slate-700">
                      {finalizedRel || "—"}
                    </td>
                    <td className="px-4 py-2.5 align-top">
                      <AcknowledgmentStateChip row={r} />
                    </td>
                    <td className="px-4 py-2.5 align-top">
                      {r.latest_reminder ? (
                        <ReminderBadge reminder={r.latest_reminder} />
                      ) : (
                        <span className="text-slate-400 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 align-top text-right text-xs text-slate-700">
                      {r.photos_count}
                    </td>
                    <td
                      className="px-4 py-2.5 align-top text-center"
                      data-testid={`rma-indicator-${r.id}`}
                    >
                      <RmaIndicator
                        count={r.rma_count || 0}
                        openCount={r.rma_open_count || 0}
                      />
                    </td>
                    <td className="px-4 py-2.5 align-top text-right">
                      <div className="flex justify-end gap-1.5">
                        {canRemind && (
                          <button
                            type="button"
                            data-testid={`dr-row-remind-${r.id}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              sendReminder(r);
                            }}
                            disabled={busyId === r.id}
                            title="Send reminder"
                            className="rounded-md border border-slate-200 hover:border-slate-400 p-1.5 transition disabled:opacity-50"
                          >
                            <PaperPlaneTiltIcon
                              size={14}
                              className="text-slate-700"
                            />
                          </button>
                        )}
                        <Link
                          to={`/work-orders/${r.work_order_id}`}
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`dr-row-openwo-${r.id}`}
                          title="Open work order"
                          className="rounded-md border border-slate-200 hover:border-slate-400 p-1.5 transition inline-flex"
                        >
                          <ArrowSquareOutIcon
                            size={14}
                            className="text-slate-700"
                          />
                        </Link>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}

function FilterChipRow({ label, options, value, onChange, testidPrefix }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mr-1 w-16">
        {label}
      </span>
      {options.map((o) => {
        const isActive = value === o.value || (!value && o.value === "");
        return (
          <button
            key={o.value || "_all"}
            type="button"
            data-testid={`${testidPrefix}-${o.value || "all"}`}
            onClick={() => onChange(o.value)}
            className={`text-xs px-2.5 py-1 rounded-md border transition ${
              isActive
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function ReminderBadge({ reminder }) {
  const tint = NOTIF_TINT[reminder.status] || NOTIF_TINT.pending;
  const ago = _relative(reminder.created_at);
  return (
    <Link
      to={`/notifications/${reminder.id}`}
      onClick={(e) => e.stopPropagation()}
      title={`${reminder.status} · ${reminder.created_at}`}
      className={`${PILL_BASE} ${tint}`}
    >
      Sent {ago}
    </Link>
  );
}

function RmaIndicator({ count, openCount }) {
  if (!count) {
    return <span className="text-slate-300">—</span>;
  }
  const noun = count === 1 ? "RMA claim" : "RMA claims";
  if (openCount > 0) {
    const tip = `${count} ${noun} (${openCount} open)`;
    return (
      <span title={tip} className="inline-flex">
        <PackageIcon size={16} weight="fill" className="text-amber-600" />
      </span>
    );
  }
  const tip = `${count} ${noun} (all closed)`;
  return (
    <span title={tip} className="relative inline-flex">
      <PackageIcon size={16} className="text-slate-400" />
      <CheckIcon
        size={9}
        weight="bold"
        className="text-emerald-600 absolute -bottom-0.5 -right-0.5 bg-white rounded-full"
      />
    </span>
  );
}

function _relative(iso) {
  if (!iso) return "";
  const dt = new Date(iso);
  const ms = Date.now() - dt.getTime();
  if (ms < 0) return new Date(iso).toLocaleString();
  const min = Math.floor(ms / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}
